/**
 * Générateur — Automatisme : connaître et utiliser les formules du volume
 * d'une pyramide ou d'un cône.
 * Attendu ID : 1241 | Niveau : 3ème | Domaine : Grandeurs et mesures (base
 * du prof ; le programme range la formule sous Représentation de l'espace,
 * cycle 4 l. 1075, PDF p. 15)
 * Rubrique : AUTOMATISME (programme 2025).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION de gen_178 (objectif 4e) aux BORNES MathsMentales 5MA3 (côtés et
 * hauteur de 2 à 6, rayon 2 à 6 et hauteur du cône 2 à 9, un facteur multiple
 * de 3 : volume entier ; V ≤ 100 cm³, coefficient de π ≤ 125) : pyramide à base carrée ou
 * rectangulaire (saisie), cône par son coefficient de π SANS la formule dans
 * l'énoncé (178 la donnait), « quel calcul ? » pour la pyramide ET le cône,
 * V/F sans la ligne d'unité. Exclus (À ARBITRER) : valeur approchée,
 * problème, base triangulaire (÷ 6), diamètre, boule.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Le volume de la pyramide » (pyramide_saisie) : base carrée
 *     (deux fois sur trois) ou rectangulaire, figure de 178, réponse en cm³.
 *   Carte 2 « Le volume du cône » (cone_texte_trou_saisie) : « $V$ = ⬚ × π
 *     cm³ », π jamais tapé.
 *   Carte 3 « Quel calcul ? » (calcul_qcm_unique) : pyramide [(6 × 6 × 5) ÷
 *     3 ; 6 × 6 × 5 ; (6 × 5) ÷ 3 ; 6 × 6 × 5 × 3] ou cône [(π × 3 × 3 × 4)
 *     ÷ 3 ; π × 3 × 3 × 4 ; (π × 3 × 4) ÷ 3 ; (2 × π × 3 × 4) ÷ 3].
 *   Carte 4 « Vrai ou faux » (sens_tableau_vf) : quatre lignes sur une
 *     pyramide à base carrée (volume, aire de la base, le tiers, le calcul).
 *
 * INVARIANTS : volume entier par construction (a²h ou L·l·h ou r²h multiple
 * de 3, throw sinon) ; distracteurs distincts de la clé ; calculs en KaTeX.
 * SACRIFIÉS : aucun (fiche « le tiers du pavé » 10 × 10 × 3 et « le tiers
 * du cylindre » 100π : côté et rayon 10 hors plage).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   oubli_du_tiers       : a × a × h (le volume du pavé), PRINCIPALE (178) ;
 *   fois_trois           : × 3 au lieu de ÷ 3 ;
 *   aire_base_confondue  : (a × h) ÷ 3 (le côté pris pour l'aire) ;
 *   rayon_non_au_carre   : (π × r × h) ÷ 3 ;
 *   perimetre_pour_aire  : (2 × π × r × h) ÷ 3, 4a pour a² ;
 *   longueur_seule       : L × h pour L × l (rectangle).
 */
import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { svgPyramide, svgCone, imgElement } from '../../4eme/grandeurs/grandeurs';

const ATTENDU_ID = 1241;
const opts = (label, svg) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label, ...(svg ? { elements_additionnels: imgElement(svg) } : {}) });

const L_PYRAMIDE = 'Le volume de la pyramide';
const L_CONE = 'Le volume du cône';
const L_CALCUL = 'Quel calcul ?';
const L_VF = 'Vrai ou faux';

const EXPLICATIONS = {
    regle: 'Volume d\'une pyramide ou d\'un cône : (aire de la base × hauteur) ÷ 3. Base carrée : côté × côté ; disque : π × rayon × rayon.',
    oubli_du_tiers: 'côté × côté × hauteur, c\'est le volume du pavé ; la pyramide en est le tiers : on divise par 3.',
    fois_trois: 'La pyramide est plus petite que le pavé de même base et de même hauteur : on divise par 3, on ne multiplie pas.',
    aire_base_confondue: 'L\'aire de la base carrée, c\'est côté × côté, pas le côté seul.',
    rayon_non_au_carre: 'L\'aire du disque de base, c\'est π × r × r : le rayon est au carré.',
    perimetre_pour_aire: '2 × π × r, c\'est le périmètre du disque ; son aire, c\'est π × r × r. De même 4 × côté est un périmètre, côté × côté une aire.',
    longueur_seule: 'L\'aire de la base rectangulaire, c\'est longueur × largeur.',
};

// ─── Tirages (bornes MathsMentales 5MA3) ──────────────────────────────────

const garde = (V, ctx, max = 100) => { if (!Number.isInteger(V) || V <= 0 || V > max) throw new Error(`gen_1241 : ${ctx}`); return V; };

/** Pyramide à base carrée : a, h dans 2..6, a²h multiple de 3. */
const tirerCarree = () => {
    for (let essai = 0; essai < 100; essai++) {
        const a = randInt(2, 6); const h = randInt(2, 6);
        if ((a * a * h) % 3 !== 0) continue;
        return { base: 'carre', a, h, aire: a * a, V: garde((a * a * h) / 3, `carrée ${a} ${h}`) };
    }
    return { base: 'carre', a: 3, h: 4, aire: 9, V: 12 };
};
/** Pyramide à base rectangulaire : L > l dans 2..6, h dans 2..6, L·l·h multiple de 3. */
const tirerRect = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randInt(3, 6); const l = randInt(2, L - 1); const h = randInt(2, 6);
        if ((L * l * h) % 3 !== 0) continue;
        return { base: 'rectangle', L, l, h, aire: L * l, V: garde((L * l * h) / 3, `rectangle ${L} ${l} ${h}`) };
    }
    return { base: 'rectangle', L: 6, l: 2, h: 5, aire: 12, V: 20 };
};
const tirerPyramide = () => (randInt(1, 3) === 1 ? tirerRect() : tirerCarree());
/** Cône : r dans 2..6, h dans 2..9, r²h multiple de 3 ; coefficient de π ≤ 125 (25 couples : variété). */
const tirerCone = () => {
    for (let essai = 0; essai < 100; essai++) {
        const r = randInt(2, 6); const h = randInt(2, 9);
        if ((r * r * h) % 3 !== 0) continue;
        return { r, h, coef: garde((r * r * h) / 3, `cône ${r} ${h}`, 125) };
    }
    return { r: 3, h: 4, coef: 12 };
};

const figurePyramide = (p) => (p.base === 'carre'
    ? svgPyramide({ base: 'carre', labels: { cote: `${p.a} cm`, h: `${p.h} cm` }, triangle: true })
    : svgPyramide({ base: 'rectangle', labels: { L: `${p.L} cm`, l: `${p.l} cm`, h: `${p.h} cm` }, triangle: true }));
const introPyramide = (p) => (p.base === 'carre'
    ? `<p>Une pyramide a une base carrée de côté ${p.a} cm et une hauteur de ${p.h} cm.</p>`
    : `<p>Une pyramide a une base rectangulaire de longueur ${p.L} cm et de largeur ${p.l} cm. Sa hauteur est de ${p.h} cm.</p>`);

// ─── Carte 1 : le volume de la pyramide (saisie) ──────────────────────────

const genPyramideSaisie = () => {
    const p = tirerPyramide();
    const payload = makeSaisie(String(p.V), `${introPyramide(p)}<p><b>Quel est son volume, en cm³ ?</b></p>`, { ...opts(L_PYRAMIDE, figurePyramide(p)), suffixe: 'cm³' });
    payload.contenu = { ...payload.contenu, sousType: p.base, explications: EXPLICATIONS };
    payload._meta.params = { ...p };
    return payload;
};

// ─── Carte 2 : le volume du cône (coefficient de π) ───────────────────────

const genConeTrou = () => {
    const c = tirerCone();
    const phrase = `Le volume de ce cône est $V$ = {{${c.coef}}} × π cm³.`;   // la lettre en KaTeX, ligne mixte (MathText)
    const payload = makeTexteTrou(phrase, `<p>Un cône a un rayon de ${c.r} cm et une hauteur de ${c.h} cm.</p><p><b>Complète</b> (π n'est pas à écrire).</p>`, { mode: 'saisie', ...opts(L_CONE, svgCone({ labels: { r: `${c.r} cm`, h: `${c.h} cm` } })) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...c };
    return payload;
};

// ─── Carte 3 : quel calcul ? (QCM) ────────────────────────────────────────

const genCalculQcm = () => {
    const cone = randInt(1, 2) === 1;
    let bon; let faux; let texte; let svg; let params;
    if (cone) {
        const c = tirerCone(); const { r, h } = c;
        bon = tex(`(\\pi \\times ${r} \\times ${r} \\times ${h}) \\div 3`);
        faux = [
            { v: tex(`\\pi \\times ${r} \\times ${r} \\times ${h}`), code: 'oubli_du_tiers' },
            { v: tex(`(\\pi \\times ${r} \\times ${h}) \\div 3`), code: 'rayon_non_au_carre' },
            { v: tex(`(2 \\times \\pi \\times ${r} \\times ${h}) \\div 3`), code: 'perimetre_pour_aire' },
        ];
        texte = `<p>Un cône a un rayon de ${r} cm et une hauteur de ${h} cm.</p><p><b>Quel calcul donne son volume, en cm³ ?</b></p>`;
        svg = svgCone({ labels: { r: `${r} cm`, h: `${h} cm` } }); params = { solide: 'cone', ...c };
    } else {
        const p = tirerPyramide();
        const base = p.base === 'carre' ? `${p.a} \\times ${p.a}` : `${p.L} \\times ${p.l}`;
        bon = tex(`(${base} \\times ${p.h}) \\div 3`);
        faux = [
            { v: tex(`${base} \\times ${p.h}`), code: 'oubli_du_tiers' },
            { v: tex(`(${p.base === 'carre' ? p.a : p.L} \\times ${p.h}) \\div 3`), code: p.base === 'carre' ? 'aire_base_confondue' : 'longueur_seule' },
            { v: tex(`${base} \\times ${p.h} \\times 3`), code: 'fois_trois' },
        ];
        texte = `${introPyramide(p)}<p><b>Quel calcul donne son volume, en cm³ ?</b></p>`;
        svg = figurePyramide(p); params = { solide: 'pyramide', ...p };
    }
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, texte, opts(L_CALCUL, svg));
    payload.contenu = { ...payload.contenu, sousType: cone ? 'cone' : 'pyramide', explications: EXPLICATIONS };
    payload._meta.params = { ...params, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const p = tirerCarree(); const { a, h, V } = p;
    const candidats = [
        { l: `Le volume de cette pyramide est ${V} cm³.`, v: true, fam: 'volume' },
        { l: `Le volume de cette pyramide est ${a * a * h} cm³.`, v: false, fam: 'volume', code: 'oubli_du_tiers' },
        { l: `L'aire de sa base est ${a * a} cm².`, v: true, fam: 'aire' },
        { l: `L'aire de sa base est ${4 * a === a * a ? a * a + a : 4 * a} cm².`, v: false, fam: 'aire', code: 'perimetre_pour_aire' },
        { l: 'Son volume est le tiers de celui du pavé de même base et de même hauteur.', v: true, fam: 'tiers' },
        { l: 'Son volume est le triple de celui du pavé de même base et de même hauteur.', v: false, fam: 'tiers', code: 'fois_trois' },
        { l: `Pour trouver son volume, on calcule ${a} × ${a} × ${h}, puis on divise par 3.`, v: true, fam: 'calcul' },
        { l: `Pour trouver son volume, on calcule ${a} × ${h}, puis on divise par 3.`, v: false, fam: 'calcul', code: 'aire_base_confondue' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), `${introPyramide(p)}<p><b>Vrai ou faux ?</b></p>`, opts(L_VF, figurePyramide(p)));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...p, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1241 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'bornes MathsMentales 5MA3 : côtés et hauteur de 2 à 6 (cône : rayon 2 à 6, hauteur 2 à 9), un facteur multiple de 3 (volume entier), V ≤ 100, coefficient de π ≤ 125',
        'π jamais tapé (coefficient) ; valeur approchée, problème, base triangulaire, diamètre et boule exclus',
    ],
    calcul_qcm_unique: 'pyramide : oubli_du_tiers (la principale), aire_base_confondue ou longueur_seule, fois_trois ; cône : oubli_du_tiers, rayon_non_au_carre, perimetre_pour_aire',
    sens_tableau_vf: 'quatre familles exclusives : volume (oubli du tiers), aire de la base (périmètre), le tiers du pavé (le triple), le calcul (côté pour aire)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    pyramide_saisie: genPyramideSaisie,
    cone_texte_trou_saisie: genConeTrou,
    calcul_qcm_unique: genCalculQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'pyramide', label: L_PYRAMIDE, formatsList: [{ key: 'pyramide_saisie', label: 'Saisie' }] },
    { id: 'cone', label: L_CONE, formatsList: [{ key: 'cone_texte_trou_saisie', label: 'Coefficient de π' }] },
    { id: 'calcul', label: L_CALCUL, formatsList: [{ key: 'calcul_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
